// ============================================================
// FireSelector - the footer's fire-mode detent rail (v8.234.0).
//
// One button per mode with a sliding indicator under the active
// detent. DOM labels are BYTE-IDENTICAL to the old mode row's
// ('S', 'Semi/3', 'Full/6', 'Standard', 'Swift', 'Lightning') -
// dozens of E2E locators click them by text; the stencil look is
// pure CSS. Tooltips price each detent with the SAME maths the
// attack spends (engine/fireModes), multipliers named.
// ============================================================
import { useLayoutEffect, useRef, useState } from 'react';
import { ammoCostPerTrigger, ammoMultiplierNotes } from '../engine/fireModes';
import clsx from '../utils/clsx';
import styles from '../styles/FireSelector.module.css';
import type { Slot } from '../types';

interface FireModeOption {
  key: string;
  label: string;
  /** Why this mode is refused right now (Pinned, or after a run), or absent: the detent is dimmed and says so. */
  refused?: string;
}

/** Melee modes carry a rules hint instead of an ammo price. */
const MELEE_MODE_RULES: Record<string, string> = {
  single: 'One attack',
  swift: 'Swift Attack - one hit per DoS',
  lightning: 'Lightning Attack - a hit per DoS, max weapon skill bonus',
};

interface Props {
  slot: Slot;
  isMelee: boolean;
  fireMode: string;
  setFireMode: (mode: string) => void;
  fireModes: FireModeOption[];
}

/** A detent's tooltip: a melee mode's rule, or a ranged mode's ammo price per trigger with its multipliers named. */
function detentTitle(slot: Slot, isMelee: boolean, key: string, label: string): string {
  if (isMelee) return MELEE_MODE_RULES[key] ?? label;
  const cost = ammoCostPerTrigger(slot, key);
  const notes = ammoMultiplierNotes(slot);
  return `${cost} round${cost === 1 ? '' : 's'} per trigger${notes.length ? ` (${notes.join(', ')})` : ''}`;
}

/**
 * Where the indicator sits: under the active (pressed) detent - measured,
 * because detent widths vary with their labels. Null with none active.
 */
function activeDetentBox(rail: HTMLDivElement): { left: number; width: number } | null {
  const active = rail.querySelector<HTMLButtonElement>('[aria-pressed="true"]');
  return active ? { left: active.offsetLeft, width: active.offsetWidth } : null;
}

export default function FireSelector({ slot, isMelee, fireMode, setFireMode, fireModes }: Props) {
  const railRef = useRef<HTMLDivElement | null>(null);
  const [indicator, setIndicator] = useState<{ left: number; width: number } | null>(null);
  useLayoutEffect(() => {
    const rail = railRef.current;
    if (rail) setIndicator(activeDetentBox(rail));
  }, [fireMode, fireModes]);
  return (
    <div className={styles.rail} ref={railRef} data-testid="fire-selector">
      <span className={styles.stencil}>{isMelee ? 'MODE' : 'SEL'}</span>
      {fireModes.map((m) => (
        <button
          key={m.key}
          className={clsx(styles.detent, fireMode === m.key && styles.detentActive, m.refused && styles.detentRefused)}
          aria-pressed={fireMode === m.key}
          title={m.refused ?? detentTitle(slot, isMelee, m.key, m.label)}
          data-refused={m.refused ? 'true' : undefined}
          onClick={() => setFireMode(m.key)}
        >
          {m.label}
        </button>
      ))}
      {indicator && <span className={styles.indicator} style={{ left: indicator.left, width: indicator.width }} />}
    </div>
  );
}
